// PRODUCT + ENVIRONMENT scope shared by the common trading views (Trading Book tabs, Active Trades).
// Kept in the URL (?product=&env=) so a link opens the same view. The product choice is offered only
// when the account trades both; the server clamps it to the account's products regardless.
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useProducts } from "@/lib/products";

export const ENVIRONMENTS = ["ALL", "LIVE", "SHADOW", "PAPER"];

export function useBookScope() {
  const [params, setParams] = useSearchParams();
  const products = useProducts();
  const both = products.futures && products.options;
  let product = (params.get("product") || "ALL").toUpperCase();
  // until the account's products are known, nothing is assumed: ALL (the server clamps it) and a
  // neutral badge - never a FUTURES fallback that an OPTIONS_ONLY account would see flash
  if (!products.loaded) product = "ALL";
  else if (!both) product = products.options && !products.futures ? "OPTIONS" : "FUTURES";
  else if (!["ALL", "FUTURES", "OPTIONS"].includes(product)) product = "ALL";
  let environment = (params.get("env") || "ALL").toUpperCase();
  if (!ENVIRONMENTS.includes(environment)) environment = "ALL";
  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "ALL") next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };
  return { product, environment, both, loaded: products.loaded, products, setProduct: (v) => set("product", v), setEnvironment: (v) => set("env", v) };
}

/** {product, environment} query params, omitting ALL. */
export function scopeParams(scope) {
  const p = {};
  if (scope?.product && scope.product !== "ALL") p.product = scope.product;
  if (scope?.environment && scope.environment !== "ALL") p.environment = scope.environment;
  return p;
}

function Seg({ value, options, onChange, testId }) {
  return (
    <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]" data-testid={testId}>
      {options.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} aria-pressed={value === k} data-testid={`${testId}-${k.toLowerCase()}`}
          className={`px-2.5 py-1 text-[11px] font-bold ${value === k ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-400 hover:text-slate-200"}`}>{label}</button>
      ))}
    </div>
  );
}

export default function BookScope({ scope }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="book-scope">
      {scope.loaded === false ? (
        <span className="px-2 py-1 rounded-sm text-[10px] font-bold border border-[#2A2F3A] text-slate-500" data-testid="scope-product-loading">…</span>
      ) : scope.both ? (
        <Seg value={scope.product} onChange={scope.setProduct} testId="scope-product"
          options={[["ALL", "All products"], ["FUTURES", "Futures"], ["OPTIONS", "Options"]]} />
      ) : (
        <span className="px-2 py-1 rounded-sm text-[10px] font-bold border border-[#2A2F3A] text-slate-300" data-testid="scope-product-fixed">
          {scope.product === "OPTIONS" ? "OPTIONS" : "FUTURES"}
        </span>
      )}
      <Seg value={scope.environment} onChange={scope.setEnvironment} testId="scope-env"
        options={[["ALL", "All"], ["LIVE", "LIVE"], ["SHADOW", "Simulated · shadow"], ["PAPER", "Simulated · paper"]]} />
    </div>
  );
}
